import { useState } from "react";
import {
  supportedFeedbackSatisfactionItems,
  supportedFeedbackTypeOptions,
} from "../config/CommonLists";
import { feedbackModel } from "../utils/Model";
import { logger } from "../utils/LogUtils";
import Modal from "@amzn/awsui-components-console/modal";
import Box from "@amzn/awsui-components-console/box";
import Button from "@amzn/awsui-components-console/button";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import TextContent from "@amzn/awsui-components-console/text-content";
import Form from "@amzn/awsui-components-console/form";
import FormField from "@amzn/awsui-components-console/form-field";
import Select from "@amzn/awsui-components-console/select";
import Textarea from "@amzn/awsui-components-console/textarea";
import RadioGroup from "@amzn/awsui-components-console/radio-group";
import Flashbar from "@amzn/awsui-components-console/flashbar";
import { ClickDetail } from "@amzn/awsui-components-console/internal/events";

export default function FeedbackModal({
  visible,
  setVisible,
}: {
  visible: boolean;
  setVisible: any;
}) {
  const FEEDBACK_TYPE_OPTIONS = supportedFeedbackTypeOptions();
  const FEEDBACK_SATISFACTION_ITEMS = supportedFeedbackSatisfactionItems();

  const [flashbarItems, setFlashBarItems] = useState([]);

  const [type, setType] = useState(FEEDBACK_TYPE_OPTIONS[0]);
  const [message, setMessage] = useState("");
  const [messageErrorText, setMessageErrorText] = useState("");
  const [satisfied, setSatisfied] = useState(
    FEEDBACK_SATISFACTION_ITEMS[0].value
  );

  const closeModal = () => {
    setType(FEEDBACK_TYPE_OPTIONS[0]);
    setMessage("");
    setSatisfied(FEEDBACK_SATISFACTION_ITEMS[0].value);
    clearErrors();
    setVisible(false);
  };

  const clearErrors = () => {
    setFlashBarItems([]);
    setMessageErrorText("");
  };

  const handleSubmit = async (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    clearErrors();

    let errors = false;

    if (message === "") {
      setMessageErrorText("Message is required");
      errors = true;
    }

    if (errors) {
      return;
    }

    try {
      const result = {
        type: type.value,
        message: message,
        satisfied:
          satisfied === FEEDBACK_SATISFACTION_ITEMS[0].value ? true : false,
      };

      await feedbackModel.insertUserFeedback(result);

      setFlashBarItems([
        {
          header: "Your user feedback was created successfully.",
          type: "success",
          dismissible: true,
          dismissLabel: "Dismiss",
          id: "create_user_feedback_success",
          onDismiss: () => setFlashBarItems([]),
        },
      ]);

      setTimeout(closeModal, 2000);
    } catch (error: any) {
      logger.error("Failed to create user feedback", error);
      setFlashBarItems([
        {
          header: "Failed to create user feedback",
          type: "error",
          content: error.message,
          dismissible: false,
          id: "create_user_feedback_error",
        },
      ]);
    }
  };

  return (
    <Modal
      onDismiss={closeModal}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Give feedback"
    >
      <Flashbar items={flashbarItems}></Flashbar>
      <form onSubmit={(e) => e.preventDefault()}>
        <Form>
          <SpaceBetween size="m" direction="vertical">
            <TextContent>
              <p>Thank you for taking time to provide feedback.</p>
            </TextContent>
            <FormField
              description="Choose the type of feedback you are submitting."
              label="Feedback type: "
            >
              <Select
                selectedOption={type}
                onChange={({ detail }) => setType(detail.selectedOption)}
                options={FEEDBACK_TYPE_OPTIONS}
              />
            </FormField>
            <FormField
              label="Enter your message: "
              errorText={messageErrorText}
            >
              <Textarea
                onChange={({ detail }) => setMessage(detail.value)}
                value={message}
                placeholder="Enter your feedback message"
              />
            </FormField>
            <FormField label="Are you satisfied with your experience? ">
              <RadioGroup
                onChange={({ detail }) => setSatisfied(detail.value)}
                value={satisfied}
                items={FEEDBACK_SATISFACTION_ITEMS}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
    </Modal>
  );
}
